import { useCallback } from "react";
import { type FitViewOptions, type Node, useReactFlow } from "@xyflow/react";

const MIN_ZOOM = 0.25;
const DEFAULT_VIEWPORT_X_PADDING = 100;
const DEFAULT_VIEWPORT_Y_PADDING = 80;
const DEFAULT_VIEWPORT_ZOOM = 0.6;

/**
 * Hook that returns a function to fit the view to the given nodes if they fit
 * within the viewport.
 *
 * If the nodes do not fit within the viewport, it will either fit to the first
 * node or reset the viewport to a default position and zoom level.
 */
export function useFitViewIfPossible(element: HTMLElement | null) {
  const { getNodesBounds, fitView, setViewport } = useReactFlow();

  return useCallback(
    (nodes: Node[], options?: FitViewOptions, fallback?: "initial" | "first_node") => {
      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      if (nodes.length === 0) {
        return;
      }

      const bounds = getNodesBounds(nodes);

      // Calculate the zoom level needed to fit all nodes
      const xZoom = rect.width / bounds.width;
      const yZoom = rect.height / bounds.height;

      // Take the smaller of the two to ensure everything fits
      const zoomToFit = Math.min(xZoom, yZoom);
      const minZoom = options?.minZoom ?? MIN_ZOOM;

      if (zoomToFit >= minZoom) {
        fitView({ ...options, nodes });
      } else if (fallback === "first_node" && nodes.length > 0) {
        fitView({ ...options, nodes: nodes.slice(0, 1) });
      } else {
        setViewport(
          {
            // eslint-disable-next-line id-length
            x: DEFAULT_VIEWPORT_X_PADDING,
            // eslint-disable-next-line id-length
            y: DEFAULT_VIEWPORT_Y_PADDING,
            zoom: DEFAULT_VIEWPORT_ZOOM,
          },
          options,
        );
      }
    },
    [fitView, getNodesBounds, element, setViewport],
  );
}
